'use client';

import * as React from 'react';
import { createPortal } from 'react-dom';

type Side = 'top' | 'bottom' | 'left' | 'right';
type Align = 'start' | 'center' | 'end';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  side?: Side;
  align?: Align;
}

const getPositionClasses = (side: Side = 'bottom', align: Align = 'center') => {
  const positions = {
    top: {
      start: 'bottom-full left-0 mb-2',
      center: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
      end: 'bottom-full right-0 mb-2',
    },
    bottom: {
      start: 'top-full left-0 mt-2',
      center: 'top-full left-1/2 -translate-x-1/2 mt-2',
      end: 'top-full right-0 mt-2',
    },
    left: {
      start: 'right-full top-0 mr-2',
      center: 'right-full top-1/2 -translate-y-1/2 mr-2',
      end: 'right-full bottom-0 mr-2',
    },
    right: {
      start: 'left-full top-0 ml-2',
      center: 'left-full top-1/2 -translate-y-1/2 ml-2',
      end: 'left-full bottom-0 ml-2',
    },
  } as const;
  
  return positions[side][align];
};

export function Tooltip({ children, content, side = 'bottom', align = 'center' }: TooltipProps) {
  const [mounted, setMounted] = React.useState(false);
  const [isVisible, setIsVisible] = React.useState(false);
  const [tooltipRect, setTooltipRect] = React.useState<DOMRect | null>(null);
  const triggerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const updateTooltipPosition = React.useCallback(() => {
    if (triggerRef.current) {
      setTooltipRect(triggerRef.current.getBoundingClientRect());
    }
  }, []);

  React.useEffect(() => {
    if (isVisible) {
      updateTooltipPosition();
      window.addEventListener('resize', updateTooltipPosition);
      window.addEventListener('scroll', updateTooltipPosition);
      return () => {
        window.removeEventListener('resize', updateTooltipPosition);
        window.removeEventListener('scroll', updateTooltipPosition);
      };
    }
  }, [isVisible, updateTooltipPosition]);

  return (
    <>
      <div 
        ref={triggerRef}
        onMouseEnter={() => {
          setIsVisible(true);
          updateTooltipPosition();
        }}
        onMouseLeave={() => setIsVisible(false)}
      >
        {children}
      </div>
      {mounted && isVisible && tooltipRect && createPortal(
        <div
          className="fixed pointer-events-none"
          style={{
            top: tooltipRect.top + window.scrollY,
            left: tooltipRect.left + window.scrollX,
            width: tooltipRect.width,
            height: tooltipRect.height,
          }}
        >
          <div
            className={`
              absolute z-[100]
              ${getPositionClasses(side, align)}
              min-w-[6rem] max-w-xs
              px-2 py-1
              bg-gray-900 text-white text-[11px]
              rounded shadow-sm
              animate-in fade-in-0 zoom-in-95
              duration-150
            `}
          >
            {content}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export function TooltipProvider({ children }: { children: React.ReactNode }) {
  return <>{children}</>;
} 